import React, { Component } from 'react';

import { SegmentedControlAPI } from '@philips-emr/tws-components-api';
import { ButtonTheme, TextAlignment } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, number, object, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { classNames, enumValues, isEnumValid, isFunction, isValidWidth, propagateData } from '@philips-emr/tws-utils';

import './segmented-control.css';

const acceptedTypes = [
  ComponentType.DATA_DISPLAY, ComponentType.LAYOUT, ComponentType.BEHAVIOR,
];

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: SegmentedControlAPI })
class SegmentedControl extends Component {
  static propTypes = {
    alignment: oneOf(enumValues(TextAlignment)),
    atLeastOne: bool.group('behaviour'),
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    data: arrayOf(object).notEnumerable(),
    expandToFill: bool.group('behaviour'),
    identifier: string,
    itemWidth: number,
    multiSelect: bool.group('behaviour'),
    onChange: eventListener,
    theme: oneOf(enumValues(ButtonTheme)),
    uuid: string,
    value: arrayOf(object).notEnumerable(),
  };

  static defaultProps = {
    alignment: TextAlignment.LEFT,
    atLeastOne: true,
    data: [],
    expandToFill: true,
    multiSelect: false,
    itemWidth: 3,
  };

  onChange = (item = {}) => {
    const { value: oldValue = [], onChange, multiSelect, atLeastOne } = this.props;

    const isItemAlreadySelected = oldValue.some(this.isEqual(item));

    if (!multiSelect && isItemAlreadySelected && atLeastOne) return;

    const value = this.getNewValue(item, oldValue, isItemAlreadySelected);

    isFunction(onChange) && onChange({ value, oldValue });
  };

  getIdentifier(item) {
    const { identifier } = this.props;

    return item[identifier];
  }

  isEqual(item) {
    return selectedValue => this.getIdentifier(selectedValue) === this.getIdentifier(item);
  }

  isDiff(item) {
    return selectedValue => this.getIdentifier(selectedValue) !== this.getIdentifier(item);
  }

  getNewValue(item, oldValue, isItemAlreadySelected) {
    const { multiSelect } = this.props;

    if (multiSelect) {
      if (isItemAlreadySelected) {
        return oldValue.filter(this.isDiff(item));
      }
      return [...oldValue, item];
    }

    if (!isItemAlreadySelected) {
      return [item];
    }

    return [];
  }

  isSelected(item) {
    const { value = [] } = this.props;

    return value.some(this.isEqual(item));
  }

  renderItem(item) {
    const { children, expandToFill, itemWidth, theme } = this.props;

    const isSelected = this.isSelected(item);

    const className = classNames({
      'c-segmented-control__component': true,
      'is-selected': isSelected,
      't-light': theme === ButtonTheme.ACCENT && isSelected,
    });

    const wrapperClassName = classNames({
      'c-segmented-control__item-wrapper': true,
      'is-flex': expandToFill,
      [`u-col-${itemWidth}`]: isValidWidth(itemWidth),
    });

    const identifier = this.getIdentifier(item);

    return (
      <div className={wrapperClassName} key={identifier}>
        <div className={className} onClick={() => this.onChange(item)}>
          {propagateData(children, item, { identifier, key: identifier }, true)}
        </div>
      </div>
    );
  }

  render() {
    const { data, uuid, alignment, theme } = this.props;
    const Container = ContainerProvider.provide(this);

    const className = classNames({
      'c-segmented-control': true,
      [`c-segmented-control--${alignment}`]: isEnumValid(TextAlignment, alignment),
      [`c-segmented-control--${theme}`]: isEnumValid(ButtonTheme, theme),
    });

    if (!Array.isArray(data) || !data.length) {
      return (
        <Container acceptedTypes={acceptedTypes} className={className} uuid={uuid} repeated ></Container>
      );
    }

    return (
      <Container acceptedTypes={acceptedTypes} className={className} uuid={uuid} repeated >
        {data.map((item) => this.renderItem(item))}
      </Container>
    );
  }
}

export default SegmentedControl;
